import { zodResolver } from '@hookform/resolvers/zod'
import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { MigrateDialogTxOverview } from '../types'
import { createTxOverview } from './createTxOverview'
import { getMigrateDialogFormValidator } from './validation'
import { TokenWithBalance } from '@/domain/common/types'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { Objective } from '@/features/actions/logic/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { AssetInputSchema } from '@/features/dialogs/common/logic/form'
import {
  getFieldsForTransferFromUserForm,
  useDebouncedFormValues,
} from '@/features/dialogs/common/logic/transfer-from-user/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { useSavingsAccountRepository } from '@/features/savings/logic/useSavingsAccountRepository'

export interface UseMigrateDialogResult {
  sdai: Token
  susds: Token
  selectableAssets: TokenWithBalance[]
  assetsFields: FormFieldsForDialog
  form: UseFormReturn<AssetInputSchema>
  objectives: Objective[]
  pageStatus: PageStatus
  migrationAmount: NormalizedNumber
  txOverview: MigrateDialogTxOverview
  actionsViewState: ActionsViewState
}

export function useUpgradeSdaiDialog(): UseMigrateDialogResult {
  const chainId = useChainId()
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { account } = useConnectedAddress()

  const tokenRepository = useSavingsTokenRepository({ chainId, account })
  const savingsAccounts = useSavingsAccountRepository({ chainId })
  const sdaiWithBalance = tokenRepository.findOneBySymbol(TokenSymbol('sDAI'))
  const susds = tokenRepository.findOneBySymbol(TokenSymbol('sUSDS'))

  const sdaiBalance = tokenRepository.findOneBalance(sdaiWithBalance.symbol)

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(getMigrateDialogFormValidator(tokenRepository)),
    defaultValues: {
      symbol: sdaiWithBalance.symbol,
      value: sdaiBalance.toFixed(),
      isMaxSelected: true,
    },
    mode: 'onChange',
  })

  const {
    debouncedFormValues: formValues,
    isDebouncing,
    isFormValid,
  } = useDebouncedFormValues({
    form,
    tokenRepository,
  })

  const objectives: Objective[] = [{ type: 'upgradeSdai', sdai: sdaiWithBalance, susds, amount: formValues.value }]
  const actionsEnabled = formValues.value.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.token.symbol, formValues.value.toString(), formValues.isMaxSelected, resetActions])

  const txOverview = createTxOverview({
    formValues,
    savingsAccounts,
    tokenRepository,
    outputToken: susds,
  })

  return {
    sdai: sdaiWithBalance,
    susds,
    selectableAssets: [{ token: sdaiWithBalance, balance: sdaiBalance }],
    assetsFields: getFieldsForTransferFromUserForm({ form, tokenRepository }),
    form,
    objectives,
    migrationAmount: formValues.value,
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    txOverview,
    actionsViewState,
  }
}
